import { supabase } from './client'

export type LocationActorRole = 'patient' | 'provider'
export type WebLocationPermissionState = 'granted' | 'prompt' | 'denied' | 'unsupported' | 'unknown'

interface LogLocationParams {
  role: LocationActorRole
  actorId: string
}

interface CacheEntry<T> {
  value: T
  expiresAt: number
}

type SettingsLookupFallback = {
  primaryColumn: string
  secondaryColumn: string
}

const unsupportedFallbackColumns = new Set<string>()

const getFallbackCacheKey = (table: string, column: string) => `${table}.${column}`

const isMissingColumnError = (error: unknown, column: string) => {
  if (!error || typeof error !== 'object') return false

  const code = 'code' in error ? String((error as { code?: unknown }).code ?? '') : ''
  const message = 'message' in error ? String((error as { message?: unknown }).message ?? '') : ''
  return code === '42703' || message.includes(column)
}

const LOCATION_LOG_INTERVAL_MS = 10 * 60 * 1000
const MIN_ACCEPTABLE_ACCURACY_METERS = 150
const CACHE_TTL_MS = 60 * 1000
const CAPTURE_WINDOW_BEFORE_MIN = 30
const CAPTURE_WINDOW_AFTER_MIN = 120

const getTodayIsoDate = () => {
  const now = new Date()
  const year = now.getFullYear()
  const month = `${now.getMonth() + 1}`.padStart(2, '0')
  const day = `${now.getDate()}`.padStart(2, '0')
  return `${year}-${month}-${day}`
}

const isInCaptureWindow = (scheduledTime?: string | null): boolean => {
  if (!scheduledTime) return true

  const parts = scheduledTime.split(':')
  const appointmentHours = Number.parseInt(parts[0], 10)
  const appointmentMinutes = Number.parseInt(parts[1] || '0', 10)

  if (Number.isNaN(appointmentHours)) {
    return true
  }

  const now = new Date()
  const currentMinutes = now.getHours() * 60 + now.getMinutes()
  const appointmentTotalMinutes = appointmentHours * 60 + appointmentMinutes

  return (
    currentMinutes >= appointmentTotalMinutes - CAPTURE_WINDOW_BEFORE_MIN &&
    currentMinutes <= appointmentTotalMinutes + CAPTURE_WINDOW_AFTER_MIN
  )
}

const getCache = <T>(key: string): T | null => {
  if (typeof window === 'undefined') return null

  try {
    const raw = window.sessionStorage.getItem(key)
    if (!raw) return null

    const entry = JSON.parse(raw) as CacheEntry<T>
    if (Date.now() > entry.expiresAt) {
      window.sessionStorage.removeItem(key)
      return null
    }

    return entry.value
  } catch {
    return null
  }
}

const setCache = <T>(key: string, value: T, ttlMs = CACHE_TTL_MS) => {
  if (typeof window === 'undefined') return

  try {
    const entry: CacheEntry<T> = {
      value,
      expiresAt: Date.now() + ttlMs,
    }

    window.sessionStorage.setItem(key, JSON.stringify(entry))
  } catch {
    // Ignore cache write failures.
  }
}

const getSettingsTableInfo = (role: LocationActorRole): { table: 'patient_settings' | 'provider_settings'; actorColumn: string; fallback: SettingsLookupFallback } => {
  if (role === 'patient') {
    return {
      table: 'patient_settings',
      actorColumn: 'patient_id',
      fallback: {
        primaryColumn: 'patient_id',
        secondaryColumn: 'patient_auth_id',
      },
    }
  }

  return {
    table: 'provider_settings',
    actorColumn: 'provider_id',
    fallback: {
      primaryColumn: 'provider_id',
      secondaryColumn: 'provider_auth_id',
    },
  }
}

const getCurrentPosition = () =>
  new Promise<GeolocationPosition>((resolve, reject) => {
    navigator.geolocation.getCurrentPosition(resolve, reject, {
      enableHighAccuracy: true,
      timeout: 15000,
      maximumAge: 60000,
    })
  })

export const locationEvidenceService = {
  intervalMs: LOCATION_LOG_INTERVAL_MS,

  async getPermissionState(): Promise<WebLocationPermissionState> {
    if (typeof window === 'undefined' || typeof navigator === 'undefined' || !('geolocation' in navigator)) {
      return 'unsupported'
    }

    try {
      if ('permissions' in navigator && navigator.permissions?.query) {
        const permission = await navigator.permissions.query({ name: 'geolocation' })
        if (permission.state === 'granted') return 'granted'
        if (permission.state === 'prompt') return 'prompt'
        if (permission.state === 'denied') return 'denied'
      }

      return 'unknown'
    } catch {
      return 'unknown'
    }
  },

  async requestForegroundPermission(): Promise<WebLocationPermissionState> {
    if (typeof window === 'undefined' || typeof navigator === 'undefined' || !('geolocation' in navigator)) {
      return 'unsupported'
    }

    try {
      await getCurrentPosition()
      return 'granted'
    } catch (error) {
      if (error instanceof GeolocationPositionError && error.code === error.PERMISSION_DENIED) {
        return 'denied'
      }

      const currentState = await this.getPermissionState()
      return currentState === 'unsupported' ? 'unsupported' : 'unknown'
    }
  },

  async isLocationTrackingEnabled(role: LocationActorRole, actorId: string): Promise<boolean> {
    const cacheKey = `locationTracking_${role}_${actorId}`
    const cached = getCache<boolean>(cacheKey)
    if (cached !== null) {
      return cached
    }

    const { table, actorColumn, fallback } = getSettingsTableInfo(role)
    const fallbackCacheKey = getFallbackCacheKey(table, fallback.secondaryColumn)

    try {
      let query = supabase
        .from(table)
        .select('location_tracking')
        .eq(actorColumn, actorId)
        .maybeSingle()

      let { data, error } = await query

      if ((error || !data) && fallback.secondaryColumn && !unsupportedFallbackColumns.has(fallbackCacheKey)) {
        const { data: authData } = await supabase.auth.getUser()
        const authId = authData.user?.id

        if (authId) {
          const fallbackResult = await supabase
            .from(table)
            .select('location_tracking')
            .eq(fallback.secondaryColumn, authId)
            .maybeSingle()

          if (isMissingColumnError(fallbackResult.error, fallback.secondaryColumn)) {
            unsupportedFallbackColumns.add(fallbackCacheKey)
          }

          data = fallbackResult.data
          error = fallbackResult.error
        }
      }

      if (error) {
        console.warn('Failed to fetch location tracking setting:', error)
        return false
      }

      const enabled = data?.location_tracking === true
      setCache(cacheKey, enabled)
      return enabled
    } catch (error) {
      console.warn('Failed to check location tracking setting:', error)
      return false
    }
  },

  async getScheduledAppointmentIdsForToday(role: LocationActorRole, actorId: string): Promise<string[]> {
    const cacheKey = `todaysAppointments_${role}_${actorId}`
    const cached = getCache<string[]>(cacheKey)
    if (cached !== null) {
      return cached
    }

    try {
      const actorColumn = role === 'patient' ? 'patient_id' : 'provider_id'
      const { data, error } = await supabase
        .from('appointments')
        .select('id, verification_mode, location_verification_mode, scheduled_time')
        .eq(actorColumn, actorId)
        .eq('scheduled_date', getTodayIsoDate())
        .in('status', ['Scheduled', 'scheduled'])

      if (error) {
        console.warn('Failed to fetch scheduled appointments for location logging:', error)
        return []
      }

      const rows = (data || []) as Array<{
        id: string
        verification_mode?: string | null
        location_verification_mode?: string | null
        scheduled_time?: string | null
      }>

      const appointmentIds = rows
        .filter((row) => {
          if (!isInCaptureWindow(row.scheduled_time)) {
            return false
          }

          if (role === 'patient') {
            return (row.verification_mode || row.location_verification_mode) !== 'destination_address'
          }

          return true
        })
        .map((row) => row.id)

      setCache(cacheKey, appointmentIds)
      return appointmentIds
    } catch (error) {
      console.warn('Failed to fetch scheduled appointments:', error)
      return []
    }
  },

  async ensureForegroundPermission(): Promise<boolean> {
    const state = await this.getPermissionState()
    if (state === 'granted') return true
    if (state === 'denied' || state === 'unsupported') return false

    const requestedState = await this.requestForegroundPermission()
    return requestedState === 'granted'
  },

  async logCurrentLocation({ role, actorId }: LogLocationParams): Promise<{ success: boolean; loggedCount: number }> {
    if (typeof window === 'undefined' || typeof navigator === 'undefined' || !('geolocation' in navigator)) {
      return { success: false, loggedCount: 0 }
    }

    try {
      const trackingEnabled = await this.isLocationTrackingEnabled(role, actorId)
      if (!trackingEnabled) {
        return { success: false, loggedCount: 0 }
      }

      const appointmentIds = await this.getScheduledAppointmentIdsForToday(role, actorId)
      if (!appointmentIds.length) {
        return { success: false, loggedCount: 0 }
      }

      const hasPermission = await this.ensureForegroundPermission()
      if (!hasPermission) {
        return { success: false, loggedCount: 0 }
      }

      const position = await getCurrentPosition()
      const { latitude, longitude, accuracy } = position.coords

      if (typeof latitude !== 'number' || typeof longitude !== 'number') {
        return { success: false, loggedCount: 0 }
      }

      if (typeof accuracy === 'number' && accuracy > MIN_ACCEPTABLE_ACCURACY_METERS) {
        return { success: false, loggedCount: 0 }
      }

      const capturedAt = new Date().toISOString()
      const insertResults = await Promise.allSettled(
        appointmentIds.map((appointmentId) =>
          supabase.rpc('insert_appointment_location_log', {
            p_appointment_id: appointmentId,
            p_actor_role: role,
            p_latitude: latitude,
            p_longitude: longitude,
            p_accuracy_meters: accuracy ?? undefined,
            p_source: 'device_gps',
            p_captured_at: capturedAt,
          })
        )
      )

      const loggedCount = insertResults.filter((result) => result.status === 'fulfilled').length
      return { success: loggedCount > 0, loggedCount }
    } catch (error) {
      console.warn('Failed to log appointment location evidence:', error)
      return { success: false, loggedCount: 0 }
    }
  },
}